import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from "../../../../hooks/reduxHooks";
import { goToDesign, goToQuantity, goToReview } from "../../../store/slices/stepSlice";

interface MobileBottomNavProps {
  currentStep: number;
  totalQuantity: number;
  totalPrice: number;
  discountPercentage: number;
  checkQuantity: (items: any, minRequired: number) => boolean;
  minRequired: number;
  items: any[];
  setShowSaveDesign: (show: boolean) => void;
}

const MobileBottomNav: React.FC<MobileBottomNavProps> = ({
  currentStep,
  totalQuantity,
  totalPrice,
  discountPercentage,
  checkQuantity,
  minRequired,
  items,
  setShowSaveDesign
}) => {
  const dispatch = useAppDispatch();
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Track scroll position to add shadow when scrolled
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Determine which button to show based on current step
  const renderActionButton = () => {
    if (currentStep === 1) {
      return (
        <button
          onClick={() => dispatch(goToReview())}
          className={`w-full uppercase py-4 px-6 rounded-full bg-primaryred text-white font-bold text-xl flex items-center justify-center transition-all ${
            !checkQuantity(items, minRequired) 
              ? 'opacity-50 cursor-not-allowed' 
              : 'transform hover:scale-[1.02] active:scale-[0.98]'
          }`}
          disabled={!checkQuantity(items, minRequired)}
        >
          <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-14a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V5a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          Calculate Pricing
        </button>
      );
    } else if (currentStep === 2) {
      return (
        <button
          onClick={() => setShowSaveDesign(true)}
          className="w-full py-4 px-6 rounded-full bg-primaryred text-white font-bold text-xl flex items-center justify-center transform hover:scale-[1.02] active:scale-[0.98] transition-all uppercase"
        >
          <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
          </svg>
          Add to Cart
        </button>
      );
    }
    return null;
  };

  // Only show the bottom nav on mobile and when we're in a relevant step (not 0)
  if (currentStep === 0) return null;

  return (
    <div className={`lg:hidden fixed bottom-0 left-0 right-0 z-[60] bg-white transition-all duration-200 ${
      isScrolled ? 'shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]' : 'border-t border-gray-200'
    }`}>
      {/* Price summary bar - only show on step 2 */}
      {currentStep === 2 && (
        <div className="px-4 py-2 bg-gray-50 flex justify-between items-center border-b border-gray-200">
          <div className="flex flex-col">
            <span className="text-xs text-gray-500">{totalQuantity} items</span>
            <div className="flex items-baseline">
              {discountPercentage > 0 && (
                <span className="line-through text-gray-400 text-xs mr-2">
                  ${(totalPrice / (1 - discountPercentage)).toFixed(2)}
                </span>
              )}
              <span className="font-bold text-lg">
                ${totalPrice.toFixed(2)}
              </span>
            </div>
          </div>
          {discountPercentage > 0 && (
            <div className="bg-green-600 text-white text-xs font-bold px-2 py-1 rounded-full">
              {(discountPercentage * 100).toFixed(0)}% OFF
            </div>
          )}
        </div>
      )}
      
      {/* Navigation bar */}
      <div className="flex items-center px-4 py-4">
        {/* Action button - now full width */}
        {renderActionButton()}
      </div>
    </div>
  );
};

export default MobileBottomNav; 